import type { WavesChart } from '@shared/wavesChart';
import type { WavesEngineState } from '@shared/engine/waves/state';
import {
  computeWavesLayout,
  obstacleCanvasX,
  playerCanvasX,
  playfieldYToCanvas,
} from './wavesPlayfieldGeometry';
import { WavesSceneRenderer } from './render/wavesDrawScene';
import { drawWavesCorridor } from './render/wavesDrawCorridor';
import { WavesObstaclesPass } from './render/wavesObstaclesPass';
import { drawWavesPlayer } from './render/wavesDrawPlayer';
import { drawWavesHud, getWavesScoreHudTarget } from './render/wavesDrawHud';
import { drawWavesOverlays } from './render/wavesDrawOverlays';
import { WavesFxSystem } from './render/wavesFx';
import { getRenderQuality, type RenderQuality } from '../rendering/renderQuality';
import {
  HIT_STOP_MAX_MS,
  MILESTONE_BURST_COUNT,
  PLAYER_TRAIL_LENGTH,
  PLAYER_TRAIL_LENGTH_LOW_TIER,
  VERDICT_TEXT_LEFT_MARGIN,
  VERDICT_TEXT_X_OFFSET,
  VERDICT_TEXT_Y_OFFSET,
} from './render/wavesRenderConstants';
import type {
  WavesParticleEmission,
  WavesRenderInput,
  WavesShakeSpec,
  WavesTrailSample,
} from './render/wavesRenderTypes';

/**
 * Top-level Waves renderer. Owns the canvas, DPR-aware sizing, the
 * particle system, the player-trail ring buffer, and screen-shake
 * state. Each `draw()` call is the single per-frame surface the
 * session needs to push state into — the renderer fans the work out
 * to the small `wavesDraw*` modules in order:
 *
 *   1. Scene background (gradient + parallax stars + corridor backdrop)
 *   2. Corridor walls
 *   3. Obstacles
 *   4. Particles + shockwaves (wake/debris, drawn BEHIND the ship)
 *   5. Player + recent trajectory trail
 *   6. HUD (score, combo, hint)
 *   7. Overlays (floating text, score pops, countdown, death)
 *
 * Subsystems are composed via constructor injection — never
 * inherited. Each owns its own state, and the renderer just sequences
 * the per-frame call order.
 */
interface WavesParticleKind {
  count: number;
  /** Lifetime in seconds. */
  life: number;
  /** Fan width (radians) the burst spreads across, centred on baseAngle. */
  spread: number;
  /** Centre direction in canvas angle (PI = left/back, -PI/2 = up). */
  baseAngle: number;
  /** Particle radius in CSS px. */
  size: number;
  speedMin: number;
  speedMax: number;
}

/**
 * Per-type burst shape for {@link WavesRenderer.emitParticles}.
 *
 * `obstacle_pass` (every perfect/good pass, and close-calls) is tuned
 * as a short BACKWARD spray: it fans left and stays off the vertical
 * axis, so the debris trails behind the ship like a speed-wake rather
 * than fanning into the lane the player is climbing/diving through.
 * Combined with the additive, translucent particle draw, this keeps
 * the sightline to incoming hazards clear when passes stack up at high
 * scroll speed.
 *
 * `death` / `milestone` keep a full radial burst — terminal or
 * celebratory beats where a big bloom is wanted and occlusion of the
 * playfield doesn't cost the player anything.
 */
const WAVES_PARTICLE_KINDS: Record<WavesParticleEmission['type'], WavesParticleKind> = {
  obstacle_pass: {
    count: 14,
    life: 0.45,
    spread: Math.PI * 0.6,
    baseAngle: Math.PI,
    size: 2.5,
    speedMin: 110,
    speedMax: 240,
  },
  death: {
    count: 36,
    life: 0.9,
    spread: Math.PI * 2,
    baseAngle: -Math.PI / 2,
    size: 3,
    speedMin: 80,
    speedMax: 220,
  },
  milestone: {
    count: 50,
    life: 1.1,
    spread: Math.PI * 2,
    baseAngle: -Math.PI / 2,
    size: 3,
    speedMin: 80,
    speedMax: 220,
  },
};

export class WavesRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private viewWidth = 0;
  private viewHeight = 0;
  private lastFrameAt = performance.now();
  private reducedMotion = false;
  private quality: RenderQuality;
  private fx: WavesFxSystem;
  private scene: WavesSceneRenderer;
  private obstaclesPass = new WavesObstaclesPass();
  /** Remaining hit-stop time (seconds). While >0, animation dt is frozen. */
  private hitStopRemaining = 0;
  private holdBoostFrame = 0;
  private readonly trailCapacity: number;
  private readonly trailSamples: WavesTrailSample[] = [];
  private trailWriteIdx = 0;
  private trailCount = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      throw new Error('Failed to acquire Waves canvas 2d context');
    }
    this.ctx = ctx;
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    this.quality = getRenderQuality();
    this.trailCapacity =
      this.quality.tier === 'low' ? PLAYER_TRAIL_LENGTH_LOW_TIER : PLAYER_TRAIL_LENGTH;
    this.fx = new WavesFxSystem(this.reducedMotion, this.quality);
    this.scene = new WavesSceneRenderer(this.quality);
    this.resize();
  }

  /**
   * Brief render-time freeze on impactful moments. Engine and audio
   * keep running — only particles, shockwaves, and overlays hold.
   */
  requestHitStop(ms: number): void {
    if (this.reducedMotion) {
      return;
    }
    const clamped = Math.min(ms, HIT_STOP_MAX_MS);
    this.hitStopRemaining = Math.max(this.hitStopRemaining, clamped / 1000);
  }

  /**
   * Recompute the canvas's backing-store dimensions to match the
   * current CSS box and the user's devicePixelRatio. Called on
   * construction and again whenever the session detects a size change
   * via its `ResizeObserver`.
   */
  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprCap);
    const cssWidth = Math.max(1, Math.floor(rect.width));
    const cssHeight = Math.max(1, Math.floor(rect.height));
    this.canvas.width = cssWidth * dpr;
    this.canvas.height = cssHeight * dpr;
    this.dpr = dpr;
    this.viewWidth = cssWidth;
    this.viewHeight = cssHeight;
    this.scene.invalidate();
  }

  getRenderDpr(): number {
    return this.dpr;
  }

  getViewSize(): { width: number; height: number } {
    return { width: this.viewWidth, height: this.viewHeight };
  }

  /**
   * Canvas-space anchor for score-pop flyers' target. Score pops
   * spawn at the obstacle position and animate toward this point.
   */
  getScoreHudAnchor(): { x: number; y: number } {
    return getWavesScoreHudTarget();
  }

  /**
   * Canvas-space anchor of the obstacle the player just passed. Used
   * by the session to spawn "+N" score-pop flyers at the visual
   * position of the obstacle on its judgement frame. Y is the
   * player's current Y (sampled from engine state) so the pop reads
   * as coming from where the player was when they cleared.
   */
  getObstacleAnchor(
    _chart: WavesChart,
    state: WavesEngineState,
    obstacleBeat: number,
    currentBeat: number,
  ): { x: number; y: number } {
    const layout = computeWavesLayout(this.viewWidth, this.viewHeight);
    return {
      x: obstacleCanvasX(layout, currentBeat, obstacleBeat),
      y: playfieldYToCanvas(layout, state.playerY),
    };
  }

  getPlayerAnchor(state: WavesEngineState): { x: number; y: number } {
    const layout = computeWavesLayout(this.viewWidth, this.viewHeight);
    return {
      x: playerCanvasX(layout),
      y: playfieldYToCanvas(layout, state.playerY),
    };
  }

  /**
   * Canvas-space anchor for verdict floating text (PERFECT / GOOD /
   * CLOSE). Sits left of and slightly above the player so the text
   * doesn't overlay the ship or block the upcoming-obstacle approach
   * lane (which runs from the right edge toward the player anchor).
   *
   * X clamps with enough left margin to fit the longest centred
   * verdict glyph plus the ±11px X-jitter `spawnFloatingText` adds,
   * so the text never clips past the left edge on narrow viewports
   * where the player anchor sits close to the playfield's left edge.
   */
  getVerdictTextAnchor(state: WavesEngineState): { x: number; y: number } {
    const layout = computeWavesLayout(this.viewWidth, this.viewHeight);
    const playerX = playerCanvasX(layout);
    const playerY = playfieldYToCanvas(layout, state.playerY);
    const minX = layout.offsetX + VERDICT_TEXT_LEFT_MARGIN;
    return {
      x: Math.max(playerX - VERDICT_TEXT_X_OFFSET, minX),
      y: playerY - VERDICT_TEXT_Y_OFFSET,
    };
  }

  /**
   * Push a particle emission into the renderer's pool. Sessions don't
   * own particles directly — they push emissions here and the
   * particle subsystem owns the lifetime, shape, and motion.
   */
  emitParticles(emission: WavesParticleEmission): void {
    if (this.reducedMotion) {
      return;
    }
    const kind = WAVES_PARTICLE_KINDS[emission.type];
    let count = kind.count;
    if (emission.type === 'milestone' && emission.tier !== undefined) {
      const tierIndex = Math.max(0, Math.min(emission.tier - 1, MILESTONE_BURST_COUNT.length - 1));
      count = MILESTONE_BURST_COUNT[tierIndex];
    }
    this.fx.emit({
      x: emission.x,
      y: emission.y,
      colour: emission.colour,
      count,
      life: kind.life,
      size: kind.size,
      angleStart: kind.baseAngle - kind.spread / 2,
      angleEnd: kind.baseAngle + kind.spread / 2,
      speedMin: kind.speedMin,
      speedMax: kind.speedMax,
    });
  }

  /** Drop all trail samples — called on loop wrap so the trail doesn't span loops. */
  clearTrail(): void {
    this.trailSamples.length = 0;
    this.trailWriteIdx = 0;
    this.trailCount = 0;
    this.obstaclesPass.reset();
  }

  /** Drop trail + particles — called on session reset / destroy. */
  resetAll(): void {
    this.trailSamples.length = 0;
    this.trailWriteIdx = 0;
    this.trailCount = 0;
    this.fx.reset();
    this.hitStopRemaining = 0;
    this.holdBoostFrame = 0;
    this.obstaclesPass.reset();
  }

  /**
   * Push the current player Y into the trail ring buffer. Called once
   * per frame from `draw()` — but exposed publicly so the session can
   * also push samples during paused/lead-in frames to keep the trail
   * "alive" before scoring kicks in.
   */
  pushTrailSample(sample: WavesTrailSample): void {
    if (this.trailCount < this.trailCapacity) {
      this.trailSamples.push(sample);
      this.trailCount += 1;
      return;
    }
    this.trailSamples[this.trailWriteIdx] = sample;
    this.trailWriteIdx = (this.trailWriteIdx + 1) % this.trailCapacity;
  }

  draw(input: WavesRenderInput): void {
    if (input.width <= 0 || input.height <= 0) {
      return;
    }
    if (
      Math.floor(input.width) !== this.viewWidth ||
      Math.floor(input.height) !== this.viewHeight
    ) {
      this.resize();
    }
    const now = performance.now();
    const wallDt = Math.min(0.05, (now - this.lastFrameAt) / 1000);
    this.lastFrameAt = now;
    let animDt: number;
    if (this.hitStopRemaining > 0) {
      this.hitStopRemaining = Math.max(0, this.hitStopRemaining - wallDt);
      animDt = 0;
    } else {
      animDt = wallDt;
    }

    for (const emission of input.particleEmissions) {
      this.emitParticles(emission);
    }
    input.particleEmissions.length = 0;
    this.fx.consumeShockwaveSpecs(input.shockwaveSpecs);
    input.shockwaveSpecs.length = 0;

    this.pushTrailSample({ beat: input.beat, playerY: input.state.playerY });

    const layout = computeWavesLayout(this.viewWidth, this.viewHeight);
    const holdBoostInterval = this.quality.tier === 'low' ? 4 : 2;

    if (input.pressing && !this.reducedMotion && input.state.alive && this.hitStopRemaining <= 0) {
      this.holdBoostFrame += 1;
      if (this.holdBoostFrame % holdBoostInterval === 0) {
        const anchorX = playerCanvasX(layout);
        const anchorY = playfieldYToCanvas(layout, input.state.playerY);
        const trailColour = input.chart.palette.player;
        this.fx.emit({
          x: anchorX - 8,
          y: anchorY - 6,
          colour: trailColour,
          count: this.quality.tier === 'low' ? 2 : 3,
          life: 0.3,
          size: 2,
          angleStart: Math.PI * 0.85,
          angleEnd: Math.PI * 1.15,
          speedMin: 30,
          speedMax: 90,
        });
      }
    } else {
      this.holdBoostFrame = 0;
    }

    const ctx = this.ctx;
    const shakeAmount = input.shake.amount;
    const biasY = input.shake.biasY ?? 0;
    const shakeX =
      this.reducedMotion || shakeAmount < 0.01 ? 0 : (Math.random() - 0.5) * shakeAmount * 2;
    const shakeY =
      this.reducedMotion || shakeAmount < 0.01
        ? 0
        : (Math.random() - 0.5 + biasY * 0.6) * shakeAmount * 2;

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.translate(shakeX, shakeY);

    this.scene.draw(
      ctx,
      layout,
      input.chart,
      input.beat,
      this.viewWidth,
      this.viewHeight,
      input.beatPulse,
    );
    drawWavesCorridor(
      ctx,
      layout,
      input.chart,
      input.beat,
      this.viewWidth,
      this.viewHeight,
      input.beatPulse,
    );
    this.obstaclesPass.draw(
      ctx,
      layout,
      input.chart,
      input.beat,
      this.quality.chromeShadows,
    );
    // Step + draw FX BEFORE the player so the ship always renders on top
    // of its own wake. Obstacle-pass debris and hold-boost embers trail
    // behind the ship instead of clouding it when passes stack up at high
    // scroll speed.
    this.fx.step(animDt);
    this.fx.draw(ctx, this.quality.chromeShadows);

    drawWavesPlayer(
      ctx,
      layout,
      input.chart,
      input.state,
      input.pressing,
      {
        samples: this.trailSamples,
        startIdx:
          this.trailCount < this.trailCapacity ? 0 : this.trailWriteIdx,
        count: this.trailCount,
        capacity:
          this.trailCount < this.trailCapacity ? this.trailCount : this.trailCapacity,
      },
      input.beatPulse,
      input.edgeAnim,
      this.quality.chromeShadows,
    );

    drawWavesHud(
      ctx,
      input.chart,
      input.state,
      input.displayedScore,
      this.viewHeight,
      input.keyLabel,
    );

    drawWavesOverlays(
      ctx,
      input.chart,
      input.state,
      input.floatingTexts,
      input.scorePops,
      input.flashes,
      input.countdown,
      this.viewWidth,
      this.viewHeight,
      animDt,
      this.quality.chromeShadows,
    );

    ctx.restore();
  }
}

/**
 * Construct an initial empty shake spec to seed a session field. Kept
 * here so the session import surface stays small (one renderer
 * import is enough).
 */
export function createEmptyWavesShake(): WavesShakeSpec {
  return { amount: 0, decay: 0 };
}
